import React, { useEffect, useRef, useState } from 'react';
import { BookingState } from '../../modules/booking-state/BookingState';
import { BookingSlotsRequest } from '../../modules/entities/Entities';
import { isDateOnlyEqual } from '../../utils/DateUtils';
import { SelectServiceDatePageContext } from './SelectServiceDatePageContext';
import { SelectServiceDatePageFormData } from './SelectServiceDatePageFormData';
import { SelectServiceDatePageFormSchema } from './SelectServiceDatePageFormShema';
import { withPageContext } from '../../modules/contexts/WithPageContext';
import { useViewProvider } from '../../modules/contexts/view-provider-context/ViewProvider';
import { FormRef } from '../../modules/forms/Form';
import { Wizard, WizardNavigate } from '../../modules/wizard/Wizard';
import { Steps } from '../../modules/wizard/Steps';
import { useGetBookingSlots } from '../../modules/booking-api/BookingApi';
import { useBookingContext } from '../../modules/booking-state/UseBookingContext';
import { SelectServiceDatePageViewProps } from './SelectServiceDatePageViewProps';
import { useModalManager } from "../../modules/contexts/modal-manager-context/ModalManagerProvider";
import { NoDateSelectedModalContent } from "../../modules/modal/NoDateSelectedModalContent/NoDateSelectedModalContent";

function addDays(date: Date, days: number): Date {
  const result = new Date(date);

  result.setDate(result.getDate() + days);

  return result;
}

function startOfDay(date: Date): Date {
  const result = new Date(date);

  result.setHours(0, 0, 0, 0);

  return result;
}

function isWeekend(date: Date): boolean {
  const sunday = 0;
  const saturday = 6;
  const day = date.getDay();

  return day === sunday || day === saturday;
}

function addBusinessDays(date: Date, businessDays: number): Date {
  let result = date;

  for (let added = 0; added < businessDays; added += 1) {
    do {
      result = addDays(result, 1);
    } while (isWeekend(result));
  }

  return result;
}

// The dealer needs two working days of lead time, so the window opens on the second
// business day from today: Monday books into Wednesday, while Thursday and Friday roll
// over the closed weekend into Monday and Tuesday. Both bounds sit on midnight because
// slots are date-granular — a "now"-based start used to cut the last day of the window
// short at the current time of day.
function calculateFirstBookableDate(today: Date): Date {
  const leadTimeInBusinessDays = 2;

  return addBusinessDays(today, leadTimeInBusinessDays);
}

function calculateMaxDate(startDate: Date): Date {
  const daysInWeek = 7;

  return addDays(startDate, daysInWeek);
}

// The page can stay open across midnight, which moves the lead time on by a day. Re-read
// the day boundary when it passes instead of freezing it.
function useStartOfToday(): Date {
  const [today, setToday] = useState(() => startOfDay(new Date()));

  useEffect(() => {
    const msUntilNextDay = Math.max(0, addDays(today, 1).getTime() - Date.now());
    const timer = setTimeout(() => setToday(startOfDay(new Date())), msUntilNextDay + 1000);

    return () => clearTimeout(timer);
  }, [today]);

  return today;
}

function ContextAccessor({
  vehicleDetails,
  vehicleServiceHistory,
  serviceRequest,
  selectedDealer,
  selectedSlot,
}: BookingState): SelectServiceDatePageContext | null {
  if (!(vehicleDetails && vehicleServiceHistory && selectedDealer && serviceRequest && vehicleDetails)) {
    return null;
  }

  return {
    selectedDealer,
    vehicleDetails,
    selectedSlot,
  };
}

export function resetSelectServiceDatePageContext(bookingState: BookingState): BookingState {
  return {
    ...bookingState,
    selectedSlot: undefined,
  };
}

export const SelectServiceDatePage = withPageContext(ContextAccessor, ({ pageContext }) => {
  const viewProvider = useViewProvider();
  const [bookingState, setBookingContext] = useBookingContext();
  const modalManager = useModalManager();
  const currentDealer = bookingState.selectedDealer || pageContext.selectedDealer;
  const today = useStartOfToday();
  // Rebuilt when the day rolls over, so a page left open overnight asks for the new
  // week instead of keeping a window whose first day has already become unbookable.
  const availableBookingsRequest = React.useMemo<BookingSlotsRequest>(() => {
    const startDate = calculateFirstBookableDate(today);

    return {
      dealerCode: currentDealer.dealerCode,
      startDate,
      endDate: calculateMaxDate(startDate),
    };
  }, [currentDealer.dealerCode, today]);
  const { data: bookingSlots } = useGetBookingSlots(availableBookingsRequest);
  // Backstop for the requested window: whatever the DMS returns, anything earlier than
  // the first bookable date never reaches the calendar. There is no matching cut at the
  // other end -- the DMS answers with more than the week that was asked for, and every
  // day of it is bookable.
  const selectableSlots = React.useMemo(
    () =>
      bookingSlots?.bookingSlots.filter((slot) => startOfDay(slot.date) >= availableBookingsRequest.startDate) ?? [],
    [bookingSlots, availableBookingsRequest],
  );
  // Upper bound of the calendar. It follows the slots that actually arrived rather than
  // the end of the requested window, so nothing the DMS offers is hidden; the requested
  // end is the floor for it, to keep the window intact while the slots are loading.
  const lastBookableDate = React.useMemo(
    () =>
      selectableSlots.reduce(
        (last, slot) => (startOfDay(slot.date) > last ? startOfDay(slot.date) : last),
        availableBookingsRequest.endDate,
      ),
    [selectableSlots, availableBookingsRequest],
  );
  const [initialFormData] = useState<SelectServiceDatePageFormData>(() => ({
    selectedServiceDate: pageContext.selectedSlot?.date,
  }));
  const formRef = useRef<FormRef<SelectServiceDatePageFormData>>(null);
  const isLoading = !bookingSlots;

  // A date picked before midnight (or carried in from the booking state) can drop out
  // of the refreshed window. Drop it too, otherwise the calendar shows nothing selected
  // while the form still holds the date and Next would store an undefined slot.
  useEffect(() => {
    if (isLoading) {
      return;
    }

    const selectedServiceDate = formRef.current?.getValues().selectedServiceDate;

    if (selectedServiceDate && !selectableSlots.some((slot) => isDateOnlyEqual(slot.date, selectedServiceDate))) {
      formRef.current!.setValue('selectedServiceDate', undefined);
    }
  }, [isLoading, selectableSlots]);

  const onNext: WizardNavigate = (nextStep) => {
    const formData = formRef.current!.getValues();

    if (!formData.selectedServiceDate) {
      modalManager.open({
        title: 'Appointment Date Required',
        content: NoDateSelectedModalContent,
        actions: [
          {
            title: 'OK',
            type: 'primary',
            key: 'ok',
            onClick(close) {
              close();
            },
          },
        ],
      });
      return;
    }

    formRef.current!.submit((data) => {
      const selectedSlot = selectableSlots.find((x) => isDateOnlyEqual(x.date, data.selectedServiceDate!))!;

      const state: BookingState = {
        ...bookingState,
        selectedSlot,
      };

      setBookingContext(state);
      nextStep(state);
    });
  };

  const onPrevious: WizardNavigate = (previousStep) => {
    previousStep(bookingState);
  };
  const onDateChange = (value: Date): void => {
    formRef.current!.setValue('selectedServiceDate', value);
  };

  const availableDates = React.useMemo(
    () => selectableSlots.map((slot) => new Date(slot.date)),
    [selectableSlots],
  );
  const viewProps: SelectServiceDatePageViewProps = {
    formRef,
    initialFormData,
    availableDates,
    firstBookableDate: availableBookingsRequest.startDate,
    lastBookableDate,
    schema: SelectServiceDatePageFormSchema,
    onDateChange,
  };

  return (
    <Wizard currentStep={Steps.Appointment} onNext={onNext} onPrevious={onPrevious} disabled={isLoading}>
      {viewProvider.renderSelectServiceDatePage(viewProps)}
    </Wizard>
  );
});
